import { Input, OverlayContainer, PrimaryButton, Radio, ReactIcons, SimpleButton } from "@kalyzee/kalyzee-ui";
import { MeetingActorDTO, MeetingCreateDTO, MeetingCreationOrigin, MeetingOutputDTO, MeetingPrivacy, MeetingSessionRole, UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useSdkContext } from "../../contexts/sdk/context";
import type { InstantMeetingSpeakingMode } from "../../helpers/settings";
import { getUserAvatar, getUserDisplayName, getUserEmail, searchUsers } from "../../helpers/user";
import { useSettings } from "../../hooks/settings";
import styles from "./CreateMeetingPopup.module.css";

/** Debounce of the user search, as in the impersonation bar. */
const SEARCH_DEBOUNCE_MS = 350;

interface FieldErrors {
  name?: string;
  password?: string;
  global?: string;
}

export interface CreateMeetingPopupProps {
  visible: boolean;
  onClose: () => void;
  onCreated: (meeting: MeetingOutputDTO) => void;
}

export const CreateMeetingPopup: React.FC<CreateMeetingPopupProps> = ({ visible, onClose, onCreated }) => {
  const sdkCtx = useSdkContext();
  const [settings] = useSettings();

  // Each option can be withdrawn on its own in the settings. A choice left with
  // a single option has nothing to ask: it is not rendered, and that option is
  // what the meeting is created with — privacy excepted while "restreint" is
  // the selected one, as its user picker lives inside that block.
  const instant = settings?.instantMeeting;
  const privacyOptions = useMemo(() => {
    const offered: MeetingPrivacy[] = [];
    if (instant?.privacy?.public !== false) offered.push(MeetingPrivacy.PUBLIC);
    if (instant?.privacy?.private !== false) offered.push(MeetingPrivacy.PRIVATE);
    if (instant?.privacy?.closed) offered.push(MeetingPrivacy.CLOSED);
    return offered;
  }, [instant?.privacy?.public, instant?.privacy?.private, instant?.privacy?.closed]);
  const speakingModeOptions = useMemo(() => {
    const offered: InstantMeetingSpeakingMode[] = [];
    if (instant?.speakingMode?.conference !== false) offered.push("conference");
    if (instant?.speakingMode?.free !== false) offered.push("free");
    return offered;
  }, [instant?.speakingMode?.conference, instant?.speakingMode?.free]);

  // Pre-selection: the configured one while it is still offered, else the first
  // option left — and the configured one alone when everything was withdrawn.
  const configuredPrivacy = instant?.privacy?.default ?? MeetingPrivacy.PUBLIC;
  const defaultPrivacy = privacyOptions.includes(configuredPrivacy)
    ? configuredPrivacy
    : privacyOptions[0] ?? configuredPrivacy;
  const configuredSpeakingMode = instant?.speakingMode?.default ?? "conference";
  const defaultSpeakingMode = speakingModeOptions.includes(configuredSpeakingMode)
    ? configuredSpeakingMode
    : speakingModeOptions[0] ?? configuredSpeakingMode;
  const defaultNewcomersAsSpectators = defaultSpeakingMode === "conference";

  // Recording is opt-in: nothing offered leaves `record` out of the creation,
  // so the organization's own default keeps applying.
  const recordOptions = useMemo(() => {
    const offered: boolean[] = [];
    if (instant?.record?.on) offered.push(true);
    if (instant?.record?.off) offered.push(false);
    return offered;
  }, [instant?.record?.on, instant?.record?.off]);
  const configuredRecord = instant?.record?.default ?? false;
  const defaultRecord: boolean | undefined = recordOptions.includes(configuredRecord)
    ? configuredRecord
    : recordOptions[0];

  const [name, setName] = useState("");
  const [privacy, setPrivacy] = useState<MeetingPrivacy>(defaultPrivacy);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [newcomersAsSpectators, setNewcomersAsSpectators] = useState(defaultNewcomersAsSpectators);
  const [record, setRecord] = useState<boolean | undefined>(defaultRecord);
  const [errors, setErrors] = useState<FieldErrors>({});
  const orgInfo = useAppStoreSelector(state => state.orgInfo);
  const currentUserId = useAppStoreSelector(state => state.user?.id);
  // A room-bound account (a classroom machine, typically) creates its meetings
  // in that room. Absent on a plain account, and then simply left out — the API
  // only accepts a room of the caller's own organization, which this one is.
  const currentUserRoomId = useAppStoreSelector(state => state.user?._room);

  // Who may join in the restricted mode.
  const [allowedUsers, setAllowedUsers] = useState<UserOutputDTO[]>([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserOutputDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchMessage, setSearchMessage] = useState<string>();
  const isClosed = privacy === MeetingPrivacy.CLOSED;

  // Reset state when the popup is closed so it opens fresh next time
  useEffect(() => {
    if (!visible) {
      setName("");
      setPrivacy(defaultPrivacy);
      setUsePassword(false);
      setPassword("");
      setNewcomersAsSpectators(defaultNewcomersAsSpectators);
      setRecord(defaultRecord);
      setAllowedUsers([]);
      setQuery("");
      setResults([]);
      setSearchMessage(undefined);
      setErrors({});
    }
  }, [visible, defaultPrivacy, defaultNewcomersAsSpectators, defaultRecord]);

  // Debounced user search, live while the restricted mode is selected. The
  // already-picked users are dropped from the hits rather than shown as
  // no-ops. Needs `user:read:any` + `identity:read:any`, hence the message
  // rather than a silent empty list when the API refuses.
  useEffect(() => {
    if (!visible || !isClosed) return;
    const cleaned = query.trim();
    if (!cleaned) {
      setResults([]);
      setSearchMessage(undefined);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const found = await searchUsers(sdkCtx.client.getHttpClient(), cleaned);
        if (cancelled) return;
        const picked = new Set(allowedUsers.map((allowed) => allowed.id));
        const remaining = found.filter((candidate) => !picked.has(candidate.id));
        setResults(remaining);
        setSearchMessage(remaining.length ? undefined : "Aucun utilisateur trouvé");
      } catch (error) {
        if (cancelled) return;
        console.error("[ERROR] searchUsers: ", error);
        setResults([]);
        setSearchMessage("Recherche impossible");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [visible, isClosed, query, allowedUsers, sdkCtx.client]);

  const addAllowedUser = useCallback((user: UserOutputDTO) => {
    setAllowedUsers((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]));
    setQuery("");
    setResults([]);
    setSearchMessage(undefined);
  }, []);

  const removeAllowedUser = useCallback((id: string) => {
    setAllowedUsers((prev) => prev.filter((user) => user.id !== id));
  }, []);

  const validate = useCallback((): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "Le nom est requis.";
    if (usePassword && !password.trim()) next.password = "Le mot de passe est requis.";
    return next;
  }, [name, usePassword, password]);

  const canSubmit = !validate().name && !validate().password;

  // The creator is always an actor of its own restricted meeting — an admin
  // one, so it may manage what it just created and can never lock itself out.
  // The others carry no role: `defaultRoleOfNewSessionByUserRole` (the speaking
  // mode) decides, as it does for everyone else.
  const closedActors = useMemo<MeetingActorDTO[]>(() => [
    ...(currentUserId ? [{ _user: currentUserId, role: MeetingSessionRole.ADMINISTRATOR }] : []),
    ...allowedUsers.filter((user) => user.id !== currentUserId).map((user) => ({ _user: user.id })),
  ], [currentUserId, allowedUsers]);

  const onSubmit = useCallback(async () => {
    const validationErrors = validate();
    if (validationErrors.name || validationErrors.password) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const payload: MeetingCreateDTO = {
        name: name.trim(),
        privacy,
        usePassword,
        // Marks the meeting as instant so the list page can find it back.
        creationOrigin: MeetingCreationOrigin.INSTANT,
        defaultRoleOfNewSessionByUserRole: {
          defaultValue: newcomersAsSpectators ? MeetingSessionRole.SPECTATOR : MeetingSessionRole.NORMAL,
        },
        ...(usePassword ? { password: password.trim() } : {}),
        ...(record !== undefined ? { record } : {}),
        ...(isClosed ? { actors: closedActors } : {}),
        ...(currentUserRoomId ? { _room: currentUserRoomId } : {}),
      };
      const res = await sdkCtx.client.getHttpClient().meeting.create(payload);
      const created = res.data;
      if (!created?._id) {
        setErrors({ global: "Le meeting a été créé mais son identifiant est introuvable." });
        return;
      }
      onCreated(created);
    } catch (err) {
      setErrors({ global: "Impossible de créer le meeting. Réessayez." });
    }
  }, [validate, name, privacy, usePassword, password, newcomersAsSpectators, record, isClosed, closedActors, currentUserRoomId, sdkCtx.client, onCreated]);

  return (
    <OverlayContainer
      visible={visible}
      style={{ position: "fixed", inset: 0, zIndex: 1000 }}
      overlayStyle={{ padding: "1em" }}
      onClose={onClose}
      overlay={
        <div className={styles.card} onClick={(e) => e.stopPropagation()}>
          <PrimaryButton className={styles.closeBtn} onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>

          <div className={styles.header}>
            <h2 className={styles.title}>Créer un meeting instantané</h2>
            <p className={styles.subtitle}>Configurez rapidement votre meeting avant de le rejoindre.</p>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Nom du meeting</label>
            <Input
              className={styles.input}
              nativeProps={{
                value: name,
                onChange: (e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                },
                placeholder: "Mathématiques — cours du mardi",
                maxLength: 120,
              }}
            />
            {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
          </div>

          {(privacyOptions.length > 1 || isClosed) && (
          <div className={styles.field}>
            <label className={styles.label}>Confidentialité</label>
            <div className={styles.modeCards}>
              {privacyOptions.includes(MeetingPrivacy.PUBLIC) && (
              <button
                type="button"
                className={clsx(styles.modeCard, privacy === MeetingPrivacy.PUBLIC && styles.modeCardSelected)}
                onClick={() => setPrivacy(MeetingPrivacy.PUBLIC)}
              >
                <Radio
                  className={styles.radio}
                  value={privacy === MeetingPrivacy.PUBLIC}
                  style={privacy !== MeetingPrivacy.PUBLIC ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
                />
                <div className={styles.modeCardText}>
                  <span className={styles.label}>Public</span>
                  <span className={styles.hint}>Tout le monde peut rejoindre.</span>
                </div>
              </button>
              )}
              {privacyOptions.includes(MeetingPrivacy.PRIVATE) && (
              <button
                type="button"
                className={clsx(styles.modeCard, privacy === MeetingPrivacy.PRIVATE && styles.modeCardSelected)}
                onClick={() => setPrivacy(MeetingPrivacy.PRIVATE)}
              >
                <Radio
                  className={styles.radio}
                  value={privacy === MeetingPrivacy.PRIVATE}
                  style={privacy !== MeetingPrivacy.PRIVATE ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
                />
                <div className={styles.modeCardText}>
                  <span className={styles.label}>Privé</span>
                  <span className={styles.hint}>Seuls les membres de l'organisation{orgInfo?.name ? <b> {orgInfo.name} </b> : ' '}peuvent rejoindre.</span>
                </div>
              </button>
              )}
              {privacyOptions.includes(MeetingPrivacy.CLOSED) && (
                <button
                  type="button"
                  className={clsx(styles.modeCard, isClosed && styles.modeCardSelected)}
                  onClick={() => setPrivacy(MeetingPrivacy.CLOSED)}
                >
                  <Radio
                    className={styles.radio}
                    value={isClosed}
                    style={!isClosed ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
                  />
                  <div className={styles.modeCardText}>
                    <span className={styles.label}>Restreint</span>
                    <span className={styles.hint}>Uniquement les personnes choisies seront autorisées à accéder à l'évènement.</span>
                  </div>
                </button>
              )}
            </div>
            {isClosed && (
              <div className={styles.allowedBlock}>
                <Input
                  className={styles.input}
                  nativeProps={{
                    value: query,
                    onChange: (e) => setQuery(e.target.value),
                    placeholder: "Rechercher (nom, email)",
                    autoComplete: "off",
                  }}
                />
                {searching && <span className={styles.hint}>Recherche…</span>}
                {!searching && searchMessage && <span className={styles.hint}>{searchMessage}</span>}
                {!!results.length && (
                  <ul className={styles.allowedResults}>
                    {results.map((candidate) => (
                      <li key={candidate.id}>
                        <button type="button" className={styles.allowedResult} onClick={() => addAllowedUser(candidate)}>
                          {/* `mode="image"` still falls back to the initials when the identity carries no avatar. */}
                          <UserAvatar
                            mode="image"
                            image={getUserAvatar(candidate)}
                            username={getUserDisplayName(candidate)}
                            // Sized inline like every other avatar of the app: the
                            // component's own class pins width/height to 100%.
                            style={{ width: "1.9em", height: "1.9em", flexShrink: 0 }}
                          />
                          <span className={styles.allowedResultText}>
                            <span>{getUserDisplayName(candidate)}</span>
                            <span className={styles.allowedResultDetail}>
                              {[candidate.role, getUserEmail(candidate)].filter((part) => !!part).join(" · ")}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {allowedUsers.length ? (
                  <div className={styles.allowedChips}>
                    {allowedUsers.map((user) => (
                      <span key={user.id} className={styles.allowedChip}>
                        <UserAvatar
                          mode="image"
                          image={getUserAvatar(user)}
                          username={getUserDisplayName(user)}
                          style={{ width: "1.7em", height: "1.7em", flexShrink: 0 }}
                        />
                        <span className={styles.allowedChipText}>
                          {getUserDisplayName(user)}
                          {getUserEmail(user) && (
                            <span className={styles.allowedChipEmail}>{getUserEmail(user)}</span>
                          )}
                        </span>
                        <button
                          type="button"
                          className={styles.allowedChipRemove}
                          onClick={() => removeAllowedUser(user.id)}
                          title="Retirer"
                        >
                          <ReactIcons.Close style={{ height: "0.6em" }} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className={styles.hint}>Sans ajout, vous serez seul à pouvoir rejoindre.</span>
                )}
              </div>
            )}
          </div>
          )}

          {/* <div className={styles.inlineField}>
            <div className={styles.inlineFieldText}>
              <span className={styles.label}>Protéger par mot de passe</span>
              <span className={styles.hint}>Un mot de passe sera demandé pour rejoindre.</span>
            </div>
            <Switch value={usePassword} onChange={(v) => {
              setUsePassword(v);
              if (!v && errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
            }} />
          </div> */}

          {/* {usePassword && (
            <div className={styles.field}>
              <label className={styles.label}>Mot de passe</label>
              <Input
                className={styles.input}
                nativeProps={{
                  value: password,
                  onChange: (e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                  },
                  type: "text",
                  placeholder: "Choisissez un mot de passe",
                  maxLength: 60,
                  autoComplete: "off",
                }}
              />
              {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
            </div>
          )} */}

          {speakingModeOptions.length > 1 && (
          <div className={styles.field}>
            <label className={styles.label}>Prise de parole</label>
            <div className={styles.modeCards}>
            <button
              type="button"
              className={clsx(styles.modeCard, newcomersAsSpectators && styles.modeCardSelected)}
              onClick={() => setNewcomersAsSpectators(true)}
            >
              <Radio
                className={styles.radio}
                value={newcomersAsSpectators}
                style={!newcomersAsSpectators ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
              />
              <div className={styles.modeCardText}>
                <span className={styles.label}>Mode conférence</span>
                <span className={styles.hint}>
                  Seuls les intervenants et les participants au tableau peuvent parler.
                </span>
              </div>
            </button>
            <button
              type="button"
              className={clsx(styles.modeCard, !newcomersAsSpectators && styles.modeCardSelected)}
              onClick={() => setNewcomersAsSpectators(false)}
            >
              <Radio
                className={styles.radio}
                value={!newcomersAsSpectators}
                style={newcomersAsSpectators ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
              />
              <div className={styles.modeCardText}>
                <span className={styles.label}>Mode libre</span>
                <span className={styles.hint}>
                  Tout le monde peut parler.
                </span>
              </div>
            </button>
            </div>
          </div>
          )}

          {recordOptions.length > 1 && (
          <div className={styles.field}>
            <label className={styles.label}>Enregistrement</label>
            <div className={styles.modeCards}>
              <button
                type="button"
                className={clsx(styles.modeCard, record === true && styles.modeCardSelected)}
                onClick={() => setRecord(true)}
              >
                <Radio
                  className={styles.radio}
                  value={record === true}
                  style={record !== true ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
                />
                <div className={styles.modeCardText}>
                  <span className={styles.label}>Enregistrer</span>
                  <span className={styles.hint}>La session sera disponible en replay.</span>
                </div>
              </button>
              <button
                type="button"
                className={clsx(styles.modeCard, record === false && styles.modeCardSelected)}
                onClick={() => setRecord(false)}
              >
                <Radio
                  className={styles.radio}
                  value={record === false}
                  style={record !== false ? { borderColor: "rgba(127, 127, 127, 0.45)" } : undefined}
                />
                <div className={styles.modeCardText}>
                  <span className={styles.label}>Ne pas enregistrer</span>
                  <span className={styles.hint}>Aucun replay ne sera produit.</span>
                </div>
              </button>
            </div>
          </div>
          )}

          {errors.global && <span className={styles.fieldError}>{errors.global}</span>}

          <SimpleButton
            className={clsx(styles.submitBtn, !canSubmit && styles.submitBtnDisabled)}
            label="Créer et rejoindre"
            onClick={onSubmit}
          />
        </div>
      }
    />
  );
};
